import Keycloak from 'keycloak-js';
import Vue from 'vue';

const prdEnv = process.env.NODE_ENV === 'production',
  jsonPath = prdEnv ? `${window.location.origin}/keycloak.json` : `${window.location.origin}/static/keycloak.json`,
  keycloak = new Keycloak(jsonPath);

const setSessionData = (key, data) => {
  sessionStorage.setItem(key, data);
};

const sessionListeners = [];

export const notifySessionExpired = () => {
  if (sessionListeners.length === 0) {
    keycloak.logout({ redirectUri: window.location.origin });
  } else {
    sessionListeners.forEach((listener) => listener.onSessionExpired());
  }
};

const isTokenExpired = () => keycloak.isTokenExpired(60);

const isRefreshTokenExpired = () => {
  const tenSeconds = 10 * 1000,
    now = new Date().getTime(),
    timeSkew = keycloak.timeSkew * 1000,
    refreshDate = new Date(keycloak.refreshTokenParsed.exp * 1000).getTime();

  let expiresIn = refreshDate;
  expiresIn += timeSkew;
  expiresIn -= now;
  expiresIn -= tenSeconds;

  return expiresIn < 0;
};

const con = console;
const setCommonAuthHeader = (token) => {
  if (Vue.http && Vue.http.headers && Vue.http.headers.common) {
    Vue.http.headers.common.Authorization = `Bearer ${token}`;
  }
};

const refreshToken = () => new Promise((resolve, reject) => {
  if (!isTokenExpired()) {
    try {
      setCommonAuthHeader(keycloak.token);
    } catch (_) {
      con.log('Erro ao setar auth header.');
    }
    resolve();
  } else if (!isRefreshTokenExpired()) {
    keycloak.updateToken(20)
      .then(() => {
        resolve();
      }).catch(() => {
        reject();
      });
  } else {
    con.log('Sessão expirada mediante expiração do token de refresh.');
    notifySessionExpired();
    reject();
  }
});

const setHeaders = (request) => {
  const token = keycloak.token || sessionStorage.getItem('token');
  request.headers.set('Authorization', `Bearer ${token}`);
  request.headers.set('Accept', 'application/json');
  request.headers.set('Content-type', 'application/json');
  setCommonAuthHeader(token);
};

let refreshInFlight = null;
export const coalescedUpdate = (request) => {
  if (refreshInFlight) {
    return refreshInFlight.then(() => {
      setHeaders(request);
      return true;
    });
  }
  refreshInFlight = refreshToken()
    .then(() => {
      setSessionData('token', keycloak.token);
      setSessionData('idToken', keycloak.idToken);
      setSessionData('refreshToken', keycloak.refreshToken);
      setHeaders(request);
      return true;
    })
    .catch((err) => {
      con.error('[KC] updateToken failed:', err);
      notifySessionExpired();
    })
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
};

export const validAndRefreshToken = () => {
  if (isTokenExpired() && !isRefreshTokenExpired()) {
    return refreshToken().then(() => {
      setSessionData('token', keycloak.token);
      setSessionData('idToken', keycloak.idToken);
      setSessionData('refreshToken', keycloak.refreshToken);
      setCommonAuthHeader(keycloak.token);
    });
  }
  return Promise.resolve();
};

export const authKeycloak = {
  init() {
    return new Promise((resolve, reject) => {
      const token = sessionStorage.getItem('token');
      const refToken = sessionStorage.getItem('refreshToken');
      const idToken = sessionStorage.getItem('idToken');

      if (token && refToken) {
        keycloak.token = token;
        keycloak.refreshToken = refToken;
        keycloak.idToken = idToken;
        setCommonAuthHeader(keycloak.token);
      }

      keycloak.init({
        onLoad: 'check-sso',
        timeSkew: 0,
        checkLoginIframe: false,
      })
        .then((authenticated) => {
          keycloak.onTokenExpired = () => {
            keycloak.updateToken(20).then(() => {
              sessionStorage.setItem('token', keycloak.token);
              sessionStorage.setItem('idToken', keycloak.idToken);
              sessionStorage.setItem('refreshToken', keycloak.refreshToken);
              setCommonAuthHeader(keycloak.token);
            }).catch(() => {
              notifySessionExpired();
            });
          };
          if (authenticated) {
            setSessionData('token', keycloak.token);
            setSessionData('idToken', keycloak.idToken);
            setSessionData('refreshToken', keycloak.refreshToken);
            setCommonAuthHeader(keycloak.token);
          }
          resolve(authenticated);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  login() {
    keycloak.login({ redirectUri: window.location.origin });
  },
  doLogout() {
    localStorage.removeItem('xTenantId');
    keycloak.logout({ redirectUri: window.location.origin });
  },
  setTokenInSession() {
    setSessionData('token', keycloak.token);
    setSessionData('idToken', keycloak.idToken);
    setSessionData('refreshToken', keycloak.refreshToken);
    setCommonAuthHeader(keycloak.token);
  },
  createInterceptors() {
    if (!Vue.http || !Vue.http.interceptors) {
      con.error('[KC] vue-resource not found; cannot install interceptor.');
      return;
    }

    const kcInterceptor = (request, next) => {
      coalescedUpdate(request)
        .then(() => {
          next();
        })
        .catch((err) => {
          con.warn('[KC] updateToken() failed before send:', err);
          notifySessionExpired();
          next(request.respondWith('', { status: 401, statusText: 'Sessão expirada' }));
        });
    };

    Vue.http.interceptors.unshift(kcInterceptor);
  },
  isTokenExpired() {
    return isTokenExpired();
  },
  registerSessionListener(listener) {
    if (listener.onSessionExpired) sessionListeners.push(listener);
  },
  validAndRefreshToken,
  notifySessionExpired,
  coalescedUpdate,
};

window.keycloak = keycloak;

const plugin = () => {
  Vue.prototype.$authKeycloak = authKeycloak;
  // Você também pode querer expor os métodos do seu objeto authKeycloak no protótipo do Vue.
  Vue.prototype.$auth = keycloak;
};

if (typeof window !== 'undefined' && window.Vue) {
  window.Vue.use(plugin);
}

export default plugin;
